import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaCalendarCheck,
  FaCalendarAlt,
  FaCheckCircle,
  FaShoppingBag,
  FaRupeeSign,
  FaEnvelope,
  FaBolt,
  FaSyncAlt,
  FaExternalLinkAlt
} from "react-icons/fa";
import { fetchWithAuth } from "../../../utils/api";
import "./TopmateBookings.css";

function TopmateOverview() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalBookings: 0,
    upcomingSessions: 0,
    completedSessions: 0,
    digitalPurchases: 0,
    totalRevenue: 0,
    emailLogsCount: 0,
    webhookLogsCount: 0
  });

  const [recentBookings, setRecentBookings] = useState([]);
  const [recentPurchases, setRecentPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadOverviewData = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await fetchWithAuth("/admin/topmate/stats");
      const data = await res.json();

      if (data.success) {
        setStats(data.stats);
        setRecentBookings(data.recentBookings || []);
        setRecentPurchases(data.recentPurchases || []);
      } else {
        setError(data.message || "Failed to load dashboard metrics");
      }
    } catch (err) {
      setError("Network error connecting to backend service");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOverviewData();
  }, []);

  const kpiCards = [
    {
      title: "Total Bookings",
      value: stats.totalBookings,
      hint: "All recorded session bookings",
      icon: <FaCalendarCheck />,
      colorClass: "cyan",
      link: "/admin/topmatebookings/recent"
    },
    {
      title: "Upcoming Sessions",
      value: stats.upcomingSessions,
      hint: "Scheduled upcoming calls",
      icon: <FaCalendarAlt />,
      colorClass: "amber",
      link: "/admin/topmatebookings/recent?status=Confirmed"
    },
    {
      title: "Completed Sessions",
      value: stats.completedSessions,
      hint: "Past & completed sessions",
      icon: <FaCheckCircle />,
      colorClass: "emerald",
      link: "/admin/topmatebookings/recent?status=Completed"
    },
    {
      title: "Digital Purchases",
      value: stats.digitalPurchases,
      hint: "Product orders received",
      icon: <FaShoppingBag />,
      colorClass: "purple",
      link: "/admin/topmatebookings/purchases"
    },
    {
      title: "Total Revenue",
      value: `₹${stats.totalRevenue.toLocaleString("en-IN")}`,
      hint: "Real payment transactions",
      icon: <FaRupeeSign />,
      colorClass: "emerald",
      link: "/admin/topmatebookings/analytics"
    },
    {
      title: "Email Logs",
      value: stats.emailLogsCount,
      hint: "Session & purchase confirmations",
      icon: <FaEnvelope />,
      colorClass: "indigo",
      // Exact mandatory route requirement
      link: "/admin/topmatebookings/emaillogs"
    },
    {
      title: "Webhook Activity",
      value: stats.webhookLogsCount,
      hint: "Total webhook events processed",
      icon: <FaBolt />,
      colorClass: "rose",
      link: "/admin/topmatebookings/webhooklogs"
    }
  ];

  return (
    <div className="topmate-dashboard-container">
      {/* HEADER BANNER */}
      <div className="topmate-header-card">
        <div className="topmate-header-text">
          <div className="topmate-header-badge">
            <span className="live-pulse-dot" />
            Topmate Bookings System
          </div>
          <h1>Topmate Bookings Overview</h1>
          <p>
            Real-time synchronization for Topmate session calls, digital product sales, meeting links, and confirmation
            emails.
          </p>
        </div>

        <div className="topmate-header-actions">
          <button className="topmate-btn-secondary" onClick={loadOverviewData} disabled={loading}>
            <FaSyncAlt className={loading ? "spin-icon" : ""} /> Refresh
          </button>
          <Link to="/admin/topmatebookings/recent" className="topmate-btn-primary">
            Manage Bookings
          </Link>
        </div>
      </div>

      {error && (
        <div className="topmate-alert danger">
          <span>{error}</span>
        </div>
      )}

      {/* KPI SUMMARY CARDS */}
      <div className="topmate-kpi-grid">
        {kpiCards.map((kpi, idx) => (
          <div
            key={idx}
            className="topmate-kpi-card"
            onClick={() => navigate(kpi.link)}
            role="button"
            tabIndex={0}
            title={`Click to view ${kpi.title}`}
          >
            <div className="kpi-card-header">
              <span className="kpi-title">{kpi.title}</span>
              <div className={`kpi-icon-wrap ${kpi.colorClass}`}>{kpi.icon}</div>
            </div>
            <div className="kpi-value">{loading ? "..." : kpi.value}</div>
            <div className="kpi-hint">
              <span>{kpi.hint}</span>
              <FaExternalLinkAlt style={{ fontSize: "0.65rem", marginLeft: "auto", opacity: 0.6 }} />
            </div>
          </div>
        ))}
      </div>

      {/* RECENT SESSION BOOKINGS TABLE */}
      <div className="topmate-card">
        <div className="card-header-row">
          <div className="card-header-title">
            <FaCalendarAlt style={{ color: "#38bdf8" }} />
            <h3>Latest Session Bookings</h3>
            <span className="card-header-badge">{recentBookings.length} latest</span>
          </div>
          <Link to="/admin/topmatebookings/recent" className="btn-action">
            View All Bookings <FaExternalLinkAlt style={{ fontSize: "0.7rem" }} />
          </Link>
        </div>

        {loading ? (
          <div className="topmate-state-box">
            <div className="topmate-spinner" />
            <p>Loading recent bookings...</p>
          </div>
        ) : recentBookings.length === 0 ? (
          <div className="topmate-state-box">
            <p>No Topmate session bookings recorded yet.</p>
            <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
              Webhooks received at <code>/api/webhooks/topmate</code> will appear here automatically.
            </span>
          </div>
        ) : (
          <div className="topmate-table-wrapper">
            <table className="topmate-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Service</th>
                  <th>Date & Time (IST)</th>
                  <th>Portfolio ID</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {recentBookings.map((b) => (
                  <tr key={b._id}>
                    <td>
                      <div>
                        <strong>{b.customerName}</strong>
                        <div style={{ fontSize: "0.75rem", color: "#94a3b8" }}>{b.customerEmail}</div>
                      </div>
                    </td>
                    <td>{b.serviceName}</td>
                    <td style={{ color: "#38bdf8" }}>{b.bookingDateIST}</td>
                    <td>
                      <code style={{ fontSize: "0.75rem", background: "rgba(255,255,255,0.08)", padding: "2px 6px", borderRadius: "4px" }}>
                        {b.portfolioBookingId}
                      </code>
                    </td>
                    <td>
                      <span className={`status-pill ${b.status?.toLowerCase()}`}>{b.status}</span>
                    </td>
                    <td>
                      <Link to="/admin/topmatebookings/recent" className="btn-action">
                        Manage
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* RECENT DIGITAL PURCHASES TABLE */}
      <div className="topmate-card">
        <div className="card-header-row">
          <div className="card-header-title">
            <FaShoppingBag style={{ color: "#c084fc" }} />
            <h3>Latest Digital Product Purchases</h3>
            <span className="card-header-badge">{recentPurchases.length} latest</span>
          </div>
          <Link to="/admin/topmatebookings/purchases" className="btn-action">
            View All Purchases <FaExternalLinkAlt style={{ fontSize: "0.7rem" }} />
          </Link>
        </div>

        {loading ? (
          <div className="topmate-state-box">
            <div className="topmate-spinner" />
            <p>Loading digital purchases...</p>
          </div>
        ) : recentPurchases.length === 0 ? (
          <div className="topmate-state-box">
            <p>No digital product purchases recorded yet.</p>
          </div>
        ) : (
          <div className="topmate-table-wrapper">
            <table className="topmate-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Product Name</th>
                  <th>Order ID</th>
                  <th>Date & Time (IST)</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentPurchases.map((p) => (
                  <tr key={p._id}>
                    <td>
                      <div>
                        <strong>{p.customerName}</strong>
                        <div style={{ fontSize: "0.75rem", color: "#94a3b8" }}>{p.customerEmail}</div>
                      </div>
                    </td>
                    <td>{p.productName}</td>
                    <td>
                      <code style={{ fontSize: "0.75rem", background: "rgba(255,255,255,0.08)", padding: "2px 6px", borderRadius: "4px" }}>
                        {p.topmateOrderId}
                      </code>
                    </td>
                    <td style={{ color: "#38bdf8" }}>{p.purchaseDateIST}</td>
                    <td style={{ color: "#10b981", fontWeight: 600 }}>
                      {p.currency} {p.amount}
                    </td>
                    <td>
                      <span className="status-pill paid">{p.paymentStatus}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default TopmateOverview;
